import classNames from 'classnames'
import { useGetImageInfo } from 'hooks/query/aiStudio/useGetImageInfo'
import { useRetryImageAnalysis } from 'hooks/query/aiStudio/useRetryImageAnalysis'
import { ClothingData } from 'pages/ai-studio/tabs/FashionEditorTab/type/type'
import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
  useMemo,
} from 'react'
import { isMobile } from 'react-device-detect'
import { RefreshCw } from 'lucide-react'
import { AvatarType } from 'service/aiStudio'

interface StyleDescriptionStepProps {
  selectedAvatar: AvatarType | null
  clothingData: ClothingData
  setClothingData: Dispatch<SetStateAction<ClothingData>>
  prevStep: () => void
  onNext?: () => void
}

interface FeatureStep {
  id: string
  type: 'top' | 'bottom'
  feature: string
  title: string
  initialValue: string
  currentValue: string
}

export const NewStyleDescriptionStep: React.FC<StyleDescriptionStepProps> = ({
  clothingData,
  setClothingData,
  prevStep,
  onNext,
}) => {
  const { data: topImageData, isLoading: topIsLoading } = useGetImageInfo(
    clothingData.topImageId,
    clothingData.topDescription,
  )
  const { data: bottomImageData, isLoading: bottomIsLoading } = useGetImageInfo(
    clothingData.bottomImageId,
    clothingData.bottomDescription,
  )

  const retryImageAnalysis = useRetryImageAnalysis()

  const [currentStepIndex, setCurrentStepIndex] = useState(0)

  // Category별 라벨 반환 함수
  const getCategoryLabel = (type: 'top' | 'bottom') => {
    if (clothingData.category === 'dress') {
      return '원피스'
    }
    return type === 'top' ? '상의' : '하의'
  }

  // 기존 props 데이터에 "스타일링 주의사항 & 액세서리" 키가 없으면 추가 (한 번만)
  useEffect(() => {
    let needsUpdate = false
    const updatedClothingData = { ...clothingData }

    // 상의 데이터 체크
    if (
      clothingData.topParsedGarmentInfo?.주요특징 &&
      !clothingData.topParsedGarmentInfo.주요특징.hasOwnProperty(
        '스타일링 주의사항 & 액세서리',
      )
    ) {
      updatedClothingData.topParsedGarmentInfo = {
        ...clothingData.topParsedGarmentInfo,
        주요특징: {
          ...clothingData.topParsedGarmentInfo.주요특징,
          '스타일링 주의사항 & 액세서리': '',
        },
      }
      needsUpdate = true
    }

    // 하의 데이터 체크
    if (
      clothingData.bottomParsedGarmentInfo?.주요특징 &&
      !clothingData.bottomParsedGarmentInfo.주요특징.hasOwnProperty(
        '스타일링 주의사항 & 액세서리',
      )
    ) {
      updatedClothingData.bottomParsedGarmentInfo = {
        ...clothingData.bottomParsedGarmentInfo,
        주요특징: {
          ...clothingData.bottomParsedGarmentInfo.주요특징,
          '스타일링 주의사항 & 액세서리': '',
        },
      }
      needsUpdate = true
    }

    if (needsUpdate) {
      setClothingData(updatedClothingData)
    }
  }, []) // 빈 의존성 배열로 한 번만 실행

  const handleRatio = (e: any) => {
    setClothingData((prev) => {
      return { ...prev, aspectRatio: e.target.value }
    })
  }

  // 스텝 생성 로직
  const featureSteps = useMemo((): FeatureStep[] => {
    const topSteps: FeatureStep[] = []
    const bottomSteps: FeatureStep[] = []

    // Top 주요특징들
    if (clothingData.topParsedGarmentInfo?.주요특징) {
      Object.keys(clothingData.topParsedGarmentInfo.주요특징).forEach((key) => {
        topSteps.push({
          id: `top-${key}`,
          type: 'top',
          feature: key,
          title: `[${getCategoryLabel('top')}] ${key}`,
          initialValue:
            clothingData.topParsedGarmentInfo?.주요특징?.[key] || '',
          currentValue:
            clothingData.topParsedGarmentInfo?.주요특징?.[key] || '',
        })
      })
    }

    // Bottom 주요특징들
    if (clothingData.bottomParsedGarmentInfo?.주요특징) {
      Object.keys(clothingData.bottomParsedGarmentInfo.주요특징).forEach(
        (key) => {
          bottomSteps.push({
            id: `bottom-${key}`,
            type: 'bottom',
            feature: key,
            title: `[${getCategoryLabel('bottom')}] ${key}`,
            initialValue:
              clothingData.bottomParsedGarmentInfo?.주요특징?.[key] || '',
            currentValue:
              clothingData.bottomParsedGarmentInfo?.주요특징?.[key] || '',
          })
        },
      )
    }

    // 카테고리에 따라 순서 결정
    if (clothingData.category === 'top' || clothingData.category === 'dress') {
      return [...topSteps, ...bottomSteps]
    } else if (clothingData.category === 'bottom') {
      return [...bottomSteps, ...topSteps]
    } else {
      // null이거나 기타 경우 기본값 (상의 먼저)
      return [...topSteps, ...bottomSteps]
    }
  }, [
    clothingData.topParsedGarmentInfo,
    clothingData.bottomParsedGarmentInfo,
    clothingData.category,
  ])
  const currentStep = featureSteps[currentStepIndex]
  const totalSteps = featureSteps.length

  // 스텝별 입력 업데이트 (원본 데이터 직접 수정)
  const updateCurrentFeature = (value: string) => {
    if (!currentStep) return

    setClothingData((prev) => {
      if (currentStep.type === 'top' && prev.topParsedGarmentInfo) {
        const featureKey = currentStep.feature
        return {
          ...prev,
          topParsedGarmentInfo: {
            ...prev.topParsedGarmentInfo,
            주요특징: {
              ...prev.topParsedGarmentInfo.주요특징,
              [featureKey]: value,
            },
          },
        }
      } else if (
        currentStep.type === 'bottom' &&
        prev.bottomParsedGarmentInfo
      ) {
        const featureKey = currentStep.feature
        return {
          ...prev,
          bottomParsedGarmentInfo: {
            ...prev.bottomParsedGarmentInfo,
            주요특징: {
              ...prev.bottomParsedGarmentInfo.주요특징,
              [featureKey]: value,
            },
          },
        }
      }
      return prev
    })
  }

  // 네비게이션 함수들
  const goToNextStep = () => {
    if (currentStepIndex < totalSteps - 1) {
      setCurrentStepIndex((prev) => prev + 1)
    }
  }

  const goToPrevStep = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex((prev) => prev - 1)
    }
  }

  const goToStep = (index: number) => {
    setCurrentStepIndex(index)
  }

  const isFirstStep = currentStepIndex === 0
  const isLastStep = currentStepIndex === totalSteps - 1

  // 다음 버튼 텍스트 결정 함수
  const getNextButtonText = () => {
    if (!currentStep) return '잘 설명했어요! 다음 단계로 >'

    const nextStep = featureSteps[currentStepIndex + 1]
    const currentLabel = getCategoryLabel(currentStep.type)
    const nextLabel = nextStep ? getCategoryLabel(nextStep.type) : ''

    // 현재 타입의 마지막 특징인지 확인
    if (currentStep.type === 'top' && (!nextStep || nextStep.type !== 'top')) {
      if (nextStep && nextStep.type === 'bottom') {
        // 상의 끝나고 하의로 넘어갈 때
        return `${currentLabel} 설명 완료! ${nextLabel} 설명으로 >`
      } else {
        // 전체 마지막 (상의만 있는 경우)
        return `${currentLabel} 마지막 특징까지 모두 잘 설명했어요!`
      }
    }

    // 하의의 마지막 특징인지 확인
    if (
      currentStep.type === 'bottom' &&
      (!nextStep || nextStep.type !== 'bottom')
    ) {
      if (nextStep && nextStep.type === 'top') {
        // 하의 끝나고 상의로 넘어갈 때
        return `${currentLabel} 설명 완료! ${nextLabel} 설명으로 >`
      } else {
        // 전체 마지막 (하의만 있거나 하의가 마지막인 경우)
        return `${currentLabel} 마지막 특징까지 모두 잘 설명했어요!`
      }
    }

    return '잘 설명했어요! 다음 단계로 >'
  }

  useEffect(() => {
    console.log('[topImageData, setClothingData]', clothingData)
    if (
      topImageData?.garmentAnalysisStatus === 'completed' &&
      !clothingData.topParsedGarmentInfo
    ) {
      setClothingData((prev: ClothingData) => {
        const parsedInfo = {
          옷:
            (topImageData.editedGarmentInfo || topImageData.parsedGarmentInfo)
              ?.옷 || '',
          주요특징:
            (topImageData.editedGarmentInfo || topImageData.parsedGarmentInfo)
              ?.주요특징 || {},
        }

        // "스타일링 주의사항 & 액세서리" 키가 없으면 추가
        if (
          !parsedInfo.주요특징.hasOwnProperty('스타일링 주의사항 & 액세서리')
        ) {
          parsedInfo.주요특징 = {
            ...parsedInfo.주요특징,
            '스타일링 주의사항 & 액세서리': '',
          }
        }

        return {
          ...prev,
          topDescription: topImageData.garmentInfo.description,
          topParsedGarmentInfo: parsedInfo,
        }
      })
    }
  }, [topImageData, setClothingData])

  useEffect(() => {
    if (
      bottomImageData?.garmentAnalysisStatus === 'completed' &&
      !clothingData.bottomParsedGarmentInfo
    ) {
      setClothingData((prev: ClothingData) => {
        const parsedInfo = {
          옷:
            (
              bottomImageData.editedGarmentInfo ||
              bottomImageData.parsedGarmentInfo
            )?.옷 || '',
          주요특징:
            (
              bottomImageData.editedGarmentInfo ||
              bottomImageData.parsedGarmentInfo
            )?.주요특징 || {},
        }

        // "스타일링 주의사항 & 액세서리" 키가 없으면 추가
        if (
          !parsedInfo.주요특징.hasOwnProperty('스타일링 주의사항 & 액세서리')
        ) {
          parsedInfo.주요특징 = {
            ...parsedInfo.주요특징,
            '스타일링 주의사항 & 액세서리': '',
          }
        }

        return {
          ...prev,
          bottomDescription: bottomImageData.garmentInfo.description,
          bottomParsedGarmentInfo: parsedInfo,
        }
      })
    }
  }, [bottomImageData, setClothingData])

  // 분석 실패 상태 체크
  const isFailed =
    topImageData?.garmentAnalysisStatus === 'failed' ||
    bottomImageData?.garmentAnalysisStatus === 'failed'

  // 해당 이미지가 있는데 분석 데이터가 없으면 분석 중 상태
  const isAnalyzing =
    (clothingData.topImageId &&
      !(topImageData?.editedGarmentInfo || topImageData?.parsedGarmentInfo) &&
      !clothingData.topParsedGarmentInfo) ||
    (clothingData.bottomImageId &&
      !(
        bottomImageData?.editedGarmentInfo || bottomImageData?.parsedGarmentInfo
      ) &&
      !clothingData.bottomParsedGarmentInfo)

  // 분석 실패 시 에러 화면 표시
  if (isFailed) {
    return (
      <div className="relative flex h-full flex-col gap-4 md:max-h-[536px]">
        {/* 헤더 섹션 */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-4">
            <h2 className="text-[18px] font-bold text-[#2E343D]">
              옷 특징 설명하기
            </h2>
          </div>
          <p className="text-base text-[#2E343D]">
            선택하신 옷의 특징을 파악해봤는데, 어떤가요? 맞는지 확인해주세요!
          </p>
        </div>

        {/* 메인 컨텐츠 영역 */}
        <div className="flex h-full flex-col gap-1 overflow-auto transition-all duration-300 ease-in-out">
          {/* 위: 이미지 영역 */}
          <div
            className={classNames('', {
              'flex items-center gap-4 rounded-[10px] bg-[#DDECFF] px-4 py-2':
                !isMobile,
              'flex flex-col items-center gap-4 rounded-[10px] bg-[#DDECFF] px-4 py-2':
                isMobile,
            })}
          >
            {/* 이미지 표시 */}
            <div
              className={classNames('', {
                'flex h-[82px] w-[66px] items-center justify-center rounded-[10px] bg-gray-200':
                  !isMobile,
                'flex h-[338px] w-[270px] items-center justify-center rounded-[10px] bg-gray-200':
                  isMobile,
              })}
            >
              {(() => {
                // category에 따라 표시할 이미지 결정
                const getDisplayImage = () => {
                  if (
                    clothingData.category === 'top' ||
                    clothingData.category === 'dress'
                  ) {
                    return clothingData.topImage || clothingData.bottomImage
                  } else if (clothingData.category === 'bottom') {
                    return clothingData.bottomImage || clothingData.topImage
                  }
                  return clothingData.topImage || clothingData.bottomImage
                }

                const displayImage = getDisplayImage()

                return displayImage && typeof displayImage === 'string' ? (
                  <img
                    src={displayImage}
                    alt="의류 이미지"
                    className="h-full w-full rounded-[10px] object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center rounded-[10px] bg-gradient-to-br from-sky-200 via-indigo-300 to-purple-400">
                    <div className="text-4xl">👕</div>
                  </div>
                )
              })()}
            </div>

            {/* 로딩 바들 */}
            <div
              className={classNames('', {
                'flex flex-1 flex-col gap-1': !isMobile,
                'flex w-full flex-col gap-1': isMobile,
              })}
            >
              <div className="h-[15px] max-w-[190px] animate-pulse rounded bg-[#C4DFFF]"></div>
              <div className="h-[15px] max-w-[352px] animate-pulse rounded bg-[#C4DFFF]"></div>
              <div className="h-[15px] max-w-[295px] animate-pulse rounded bg-[#C4DFFF]"></div>
            </div>
          </div>

          {/* 에러 메시지 영역 */}
          <div className="flex flex-col items-end gap-4 rounded-[10px] bg-[#F6F8FB] p-4 md:min-h-[340px] md:w-[564px]">
            <div className="flex h-full w-full flex-col items-center justify-center gap-4">
              {/* 에러 이모지 */}
              <div className="text-[32px] font-bold text-[#FD6767]">🥺</div>

              {/* 에러 메시지 */}
              <p className="text-center text-sm font-bold leading-[1.2] text-[#FD6767]">
                앗! 분석 AI 혹은 네트워크 오류 등의 문제로
                <br />
                선택하신 옷의 특징을 분석하는데 실패했어요.
                <br />
                아래 버튼을 클릭해서 다시 시도해주세요!
              </p>

              {/* 재시도 버튼 */}
              <button
                onClick={() => {
                  // 실패한 이미지 ID 찾기
                  console.log(topImageData, bottomImageData)
                  const failedImageId =
                    topImageData?.garmentAnalysisStatus === 'failed'
                      ? clothingData.topImageId
                      : bottomImageData?.garmentAnalysisStatus === 'failed'
                        ? clothingData.bottomImageId
                        : null

                  if (failedImageId) {
                    retryImageAnalysis.mutate(failedImageId)
                  }
                }}
                disabled={retryImageAnalysis.isPending}
                className="flex h-[44px] w-[160px] items-center justify-center gap-2.5 rounded-[10px] bg-[#FD6767] px-6 text-sm font-bold text-white transition-colors hover:bg-[#E55B5B] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {/* 새로고침 아이콘 */}
                <RefreshCw size={20} className="text-white" />
                {retryImageAnalysis.isPending
                  ? '재시도 중...'
                  : '분석 다시하기'}
              </button>
            </div>
          </div>
        </div>

        {/* 네비게이션 버튼 (비활성화) */}
        <div
          className={classNames('', {
            'fixed bottom-0 left-0 right-0 z-50 flex h-[90px] gap-1 border-t border-[#EEF1F7] bg-white px-4 pb-6 pt-2':
              isMobile,
            'absolute -bottom-[50px] flex w-full justify-end gap-1': !isMobile,
          })}
        >
          <button
            onClick={prevStep}
            className="h-[54px] min-w-[160px] rounded-[10px] bg-[#EEF1F7] px-6 py-3 text-sm font-bold text-[#5E6068] transition-colors hover:bg-gray-300"
          >
            이전
          </button>
          <button
            disabled
            className="h-[54px] min-w-[160px] cursor-not-allowed rounded-[10px] bg-gray-300 px-6 py-3 text-sm font-bold text-gray-500"
          >
            분석 실패
          </button>
        </div>
      </div>
    )
  }

  if (isAnalyzing) {
    return (
      <div className="relative flex h-full flex-col gap-4 md:max-h-[536px]">
        {/* 헤더 섹션 */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-4">
            <h2 className="text-[18px] font-bold text-[#2E343D]">
              옷 특징 설명하기
            </h2>
            {/* Aspect Ratio 선택 */}
          </div>
          <p className="text-base text-[#2E343D]">
            선택하신 옷의 특징을 파악해봤는데, 어떤가요? 맞는지 확인해주세요!
          </p>
        </div>

        {/* 메인 컨텐츠 영역 */}
        <div className="flex h-full flex-col gap-1 overflow-auto transition-all duration-300 ease-in-out">
          {/* 위: 이미지 영역 */}
          {/* <div className="flex items-center gap-4 rounded-[10px] bg-[#DDECFF] px-4 py-2"> */}
          <div
            className={classNames('', {
              'flex items-center gap-4 rounded-[10px] bg-[#DDECFF] px-4 py-2':
                !isMobile,
              'py-2" flex flex-col items-center gap-4 rounded-[10px] bg-[#DDECFF] p-4':
                isMobile,
            })}
          >
            {/* 이미지 표시 */}
            <div
              className={classNames('', {
                'flex h-[82px] w-[66px] items-center justify-center rounded-[10px] bg-gray-200':
                  !isMobile,
                'flex h-[338px] w-[270px] items-center justify-center rounded-[10px] bg-gray-200':
                  isMobile,
              })}
            >
              {(() => {
                // category에 따라 표시할 이미지 결정
                const getDisplayImage = () => {
                  if (
                    clothingData.category === 'top' ||
                    clothingData.category === 'dress'
                  ) {
                    return clothingData.topImage || clothingData.bottomImage
                  } else if (clothingData.category === 'bottom') {
                    return clothingData.bottomImage || clothingData.topImage
                  }
                  return clothingData.topImage || clothingData.bottomImage
                }

                const displayImage = getDisplayImage()

                return displayImage && typeof displayImage === 'string' ? (
                  <div className="relative h-full w-full">
                    <img
                      src={displayImage}
                      alt="의류 이미지"
                      className="h-full w-full rounded-[10px] object-cover"
                    />
                    {/* 분석 중 오버레이 */}
                    <div className="absolute inset-0 flex items-center justify-center rounded-[10px] bg-black/20">
                      <div className="h-8 w-8 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                    </div>
                  </div>
                ) : (
                  <div className="flex h-full w-full items-center justify-center rounded-[10px] bg-gradient-to-br from-sky-200 via-indigo-300 to-purple-400">
                    <div className="h-full w-full animate-pulse rounded-[10px] bg-gradient-to-r from-transparent via-white/40 to-transparent"></div>
                  </div>
                )
              })()}
            </div>

            {/* 분석 중 텍스트와 로딩 바 */}
            <div
              className={classNames('', {
                'flex flex-1 flex-col gap-1': !isMobile,
                'flex w-full flex-col gap-1': isMobile,
              })}
            >
              <div className="mb-2 text-base font-bold text-[#3B61B3]">
                옷의 특징을 분석하고 있어요....
              </div>
              {/* 로딩 바들 */}
              <div className="h-[15px] max-w-[190px] animate-pulse rounded bg-[#C4DFFF]"></div>
              <div className="h-[15px] max-w-[352px] animate-pulse rounded bg-[#C4DFFF]"></div>
              <div className="h-[15px] max-w-[295px] animate-pulse rounded bg-[#C4DFFF]"></div>
            </div>
          </div>

          {!isMobile && (
            <div className="flex flex-col gap-4 rounded-[10px] bg-[#F6F8FB] p-4 md:min-h-[340px] md:w-[564px]">
              {/* 상의 라벨 */}
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center rounded bg-[#FF69AC] px-2 py-0.5 text-sm font-bold text-white">
                  상의
                </span>
              </div>

              {/* 비활성화된 텍스트 입력 영역 */}
              <div className="flex-1 rounded-[10px] border border-[#D9DEE8] bg-white p-4">
                <div className="flex h-[130px] w-full flex-col justify-center gap-1">
                  {/* 스켈레톤 라인들 */}
                  <div className="h-[15px] animate-pulse rounded bg-[#EEF1F7]"></div>
                  <div
                    className="h-[15px] animate-pulse rounded bg-[#EEF1F7]"
                    style={{ width: '90%' }}
                  ></div>
                  <div
                    className="h-[15px] animate-pulse rounded bg-[#EEF1F7]"
                    style={{ width: '95%' }}
                  ></div>
                </div>
              </div>

              {/* 도움말 텍스트 (투명도 0으로 숨김) */}
              <p className="text-center text-sm font-bold text-[#6B8ACB] opacity-0">
                전문적이고 명확한 용어를 사용해서 섬세하게 설명하면, AI가 훨씬
                잘 이해해요 🌟
              </p>
            </div>
          )}
        </div>

        {/* 네비게이션 버튼 (비활성화)    'fixed bottom-0 left-0 right-0 z-50 h-[90px] border-t border-[#EEF1F7] bg-white px-4 pb-6 pt-2': */}
        <div
          className={classNames('', {
            'fixed bottom-0 left-0 right-0 z-50 flex h-[90px] gap-1 border-t border-[#EEF1F7] bg-white px-4 pb-6 pt-2':
              isMobile,
            'absolute -bottom-[50px] flex w-full justify-end gap-1': !isMobile,
          })}
        >
          <button
            disabled
            className="h-[54px] min-w-[160px] cursor-not-allowed rounded-[10px] bg-gray-300 px-6 py-3 text-sm font-bold text-gray-500"
          >
            이전
          </button>
          <button
            disabled
            className="h-[54px] min-w-[160px] cursor-not-allowed rounded-[10px] bg-gray-300 px-6 py-3 text-sm font-bold text-gray-500"
          >
            분석 중...
          </button>
        </div>
      </div>
    )
  }

  // 상의 최종 설명 텍스트 생성
  const generateFinalTopDescription = () => {
    const topInfo = clothingData.topParsedGarmentInfo
    if (!topInfo) return ''

    let result = `n주요특징:\n`

    if (topInfo.주요특징) {
      Object.entries(topInfo.주요특징).forEach(([key, value]) => {
        result += `${key}: "${value}"\n`
      })
    }

    return result
  }

  // 하의 최종 설명 텍스트 생성
  const generateFinalBottomDescription = () => {
    const bottomInfo = clothingData.bottomParsedGarmentInfo
    if (!bottomInfo) return ''

    let result = `주요특징:\n`

    if (bottomInfo.주요특징) {
      Object.entries(bottomInfo.주요특징).forEach(([key, value]) => {
        result += `${key}: "${value}"\n`
      })
    }

    return result
  }

  return (
    <div className="relative flex h-full flex-col gap-4 md:max-h-[536px]">
      {/* 헤더 섹션 */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-4">
          <h2 className="text-[18px] font-bold text-[#2E343D]">
            옷 특징 설명하기
          </h2>
          {/* Aspect Ratio 선택 */}
          {/* <select
            className="rounded-xl border-[4px] border-main md:w-[200px]"
            onChange={handleRatio}
            value={clothingData.aspectRatio}
          >
            <option>9:16</option>
            <option>4:5</option>
            <option>1:1</option>
          </select> */}
        </div>
        <p className="text-base text-[#2E343D]">
          선택하신 옷의 특징을 파악해봤는데, 어떤가요? 맞는지 확인해주세요!
        </p>
      </div>

      {/* 진행도 표시 */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-0">
        <div className="text-sm text-gray-600">
          Step {currentStepIndex + 1} of {totalSteps}
        </div>
        <div className="flex flex-wrap gap-1">
          {Array.from({ length: totalSteps }).map((_, index) => (
            <button
              key={index}
              onClick={() => goToStep(index)}
              title={featureSteps[index]?.title || `Step ${index + 1}`}
              className={`h-2 w-8 cursor-pointer rounded-full transition-all duration-300 hover:scale-110 hover:shadow-md ${
                index <= currentStepIndex ? 'bg-[#6B8ACB]' : 'bg-gray-200'
              }`}
              tabIndex={0}
              aria-label={`${featureSteps[index]?.title || `Step ${index + 1}`}로 이동`}
            />
          ))}
        </div>
      </div>

      {/* 메인 컨텐츠 영역 - 스텝 기반 레이아웃 */}
      <div className="flex h-full flex-col gap-1 overflow-auto transition-all duration-300 ease-in-out">
        {/* 위: 이미지 영역 */}
        <div
          className={classNames('', {
            'flex items-center gap-4 rounded-[10px] bg-[#DDECFF] px-4 py-2':
              !isMobile,
            'flex flex-col items-center gap-4 rounded-[10px] bg-[#DDECFF] px-4 py-2':
              isMobile,
          })}
        >
          {/* 이미지 표시 */}
          <div
            className={classNames('', {
              'flex h-[82px] w-[66px] items-center justify-center rounded-[10px] bg-gray-200':
                !isMobile,
              'flex h-[338px] w-[270px] items-center justify-center rounded-[10px] bg-gray-200':
                isMobile,
            })}
          >
            {(currentStep?.type === 'top'
              ? clothingData.topImage
              : clothingData.bottomImage) &&
            typeof (currentStep?.type === 'top'
              ? clothingData.topImage
              : clothingData.bottomImage) !== 'object' ? (
              <img
                src={
                  currentStep?.type === 'top'
                    ? (clothingData.topImage as string)
                    : (clothingData.bottomImage as string)
                }
                alt="의류 이미지"
                className="h-full w-full rounded-[10px] object-cover"
              />
            ) : (
              <div className="text-4xl">
                {currentStep?.type === 'top' ? '👕' : '👖'}
              </div>
            )}
          </div>

          {/* 전체 설명 텍스트 */}
          <div className="max-w-[300px] text-base text-[#3B61B3]">
            {`선택하신 ${currentStep ? getCategoryLabel(currentStep.type) : ''}의 특징을 아래에서 확인하세요!`}
            {/* {currentStep?.feature === '스타일링 주의사항 & 액세서리'
              ? currentStep?.type === 'top'
                ? '모자, 안경, 귀걸이 등 액세서리가 더해지면스타일샷의 느낌이 훨씬 풍부해지고 내 옷이 한 층 돋보여요!'
                : '하의 핏과 신발은 패션의 완성. 최종 완성도를 위한 스타일링 센스를 발휘해보세요!'
              : currentStep?.type === 'top'
                ? clothingData.topParsedGarmentInfo?.옷 ||
                  (
                    topImageData?.editedGarmentInfo ||
                    topImageData?.parsedGarmentInfo
                  )?.옷 ||
                  `${getCategoryLabel('top')} 정보를 분석 중입니다...`
                : clothingData.bottomParsedGarmentInfo?.옷 ||
                  (
                    bottomImageData?.editedGarmentInfo ||
                    bottomImageData?.parsedGarmentInfo
                  )?.옷 ||
                  `${getCategoryLabel('bottom')} 정보를 분석 중입니다...`} */}
          </div>
        </div>

        {/* 아래: 현재 스텝 텍스트 입력 영역 */}
        <div className="flex flex-col gap-4 rounded-[10px] bg-[#F6F8FB] p-4 md:w-[564px]">
          {/* 현재 스텝 제목 */}
          <div className="flex items-center gap-2">
            <span
              className={`inline-flex items-center justify-center rounded px-2 py-0.5 text-sm font-bold text-white ${
                currentStep?.type === 'top' ? 'bg-[#FF69AC]' : 'bg-[#3E599D]'
              }`}
            >
              {currentStep ? getCategoryLabel(currentStep.type) : ''}
            </span>
            <span className="text-base font-bold text-[#2E343D]">
              {currentStep?.feature}
            </span>
          </div>

          {/* 텍스트 입력 영역 */}
          <div className="flex-1 rounded-[10px] border border-[#D9DEE8] bg-white p-4">
            <textarea
              className="h-[130px] w-full resize-none text-base outline-none"
              placeholder={`${currentStep?.feature}에 대해 자세히 설명해주세요...`}
              value={
                currentStep?.currentValue || currentStep?.initialValue || ''
              }
              onChange={(e) => updateCurrentFeature(e.target.value)}
            />
          </div>

          {/* 도움말 텍스트 */}
          <p className="text-center text-sm font-bold text-[#6B8ACB]">
            전문적이고 명확한 용어를 사용해서 섬세하게 설명하면, AI가 훨씬 잘
            이해해요 🌟
          </p>
        </div>
      </div>
      {/* 네비게이션 버튼 */}
      {/* <div className="absolute -bottom-[50px] flex w-full justify-end gap-1"> */}
      <div
        className={classNames('', {
          'fixed bottom-0 left-0 right-0 z-50 flex h-[90px] justify-between gap-1 border-t border-[#EEF1F7] bg-white px-4 pb-6 pt-2':
            isMobile,
          'absolute -bottom-[50px] flex w-full justify-end gap-1': !isMobile,
        })}
      >
        <button
          onClick={() => {
            isFirstStep ? prevStep() : goToPrevStep()
          }}
          className={classNames(
            'h-[54px] rounded-[10px] bg-[#EEF1F7] px-4 py-3 text-sm font-bold text-[#5E6068] transition-colors',
            {
              'bg-[#EEF1F7] text-[#5E6068] hover:bg-gray-300': isFirstStep,
              'min-w-[160px]': !isMobile,
              'w-[160px] flex-1': isMobile,
            },
          )}
        >
          이전
        </button>

        <button
          onClick={() => {
            if (isLastStep) {
              // 마지막 스텝에서는 최종 데이터 생성 및 저장
              if (onNext) {
                const finalTopDescription = generateFinalTopDescription()
                const finalBottomDescription = generateFinalBottomDescription()

                setClothingData((prev) => ({
                  ...prev,
                  topDescription: finalTopDescription,
                  bottomDescription: finalBottomDescription,
                  generatedImages: [],
                }))

                onNext()
              }
            } else {
              goToNextStep()
            }
          }}
          className="flex-2 h-[54px] rounded-[10px] bg-[#00B7B9] px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-[#00A5A7]"
        >
          {getNextButtonText()}
        </button>
      </div>
    </div>
  )
}
